import { useCallback, useEffect, useRef, useState } from 'react';
import { useLazyGetSubModuleFiltersQuery } from '@api/userModules';
import { SubmoduleFilter, SubmoduleFilterMap } from '@stores/Modules/types/modules';
import { buildFilterMap } from '../utils/utils';

export type SubmoduleFilterRegistry = Map<string, SubmoduleFilterMap>;

interface UseSubmoduleFilterRegistryResult {
  filterRegistry: SubmoduleFilterRegistry;
  isLoading: boolean;
  refreshSubmoduleFilters: (submoduleId: string) => Promise<void>;
}

/**
 * Pre-fetches filters for all provided submodule IDs in parallel and
 * maintains a registry (Map<submoduleId, SubmoduleFilterMap>).
 *
 * `refreshSubmoduleFilters` bypasses the RTK cache — use it after add-game
 * to keep the registry in sync.
 */
export const useSubmoduleFilterRegistry = (
  submoduleIds: string[],
): UseSubmoduleFilterRegistryResult => {
  const [getSubModuleFilters] = useLazyGetSubModuleFiltersQuery();
  const [filterRegistry, setFilterRegistry] = useState<SubmoduleFilterRegistry>(new Map());
  const [isLoading, setIsLoading] = useState(false);

  // Stable ref so the fetch effect doesn't re-run when the function identity changes.
  const fetchOne = useCallback(
    async (submoduleId: string, preferCache: boolean): Promise<void> => {
      try {
        const result = await getSubModuleFilters(submoduleId, preferCache).unwrap();
        const filters: SubmoduleFilter[] = result?.data?.submoduleFilterValues ?? [];
        setFilterRegistry((prev) => {
          const next = new Map(prev);
          next.set(submoduleId, buildFilterMap(filters));
          return next;
        });
      } catch {
        // Individual fetch failures are silent — the registry simply won't have
        // an entry for that submodule (paste validation will fall back safely).
      }
    },
    [getSubModuleFilters],
  );

  // Track the submodule IDs as a stable JSON string to avoid spurious re-fetches.
  const idsKey = submoduleIds.join(',');
  const prevIdsKeyRef = useRef<string>('');

  useEffect(() => {
    if (!submoduleIds.length || idsKey === prevIdsKeyRef.current) return;
    prevIdsKeyRef.current = idsKey;

    setIsLoading(true);
    Promise.all(submoduleIds.map((id) => fetchOne(id, true))).finally(() => {
      setIsLoading(false);
    });
  }, [idsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshSubmoduleFilters = useCallback(
    async (submoduleId: string): Promise<void> => {
      await fetchOne(submoduleId, false);
    },
    [fetchOne],
  );

  return { filterRegistry, isLoading, refreshSubmoduleFilters };
};
